<template>
	<div>
		<p>
			Welcome to the tutorial series for the FarmD Character Importer. The importer is a Unity asset package that will guide you through the steps for getting a rigged character 
			model set up with the various components and settings that the game needs to bring your character to life.
		</p>

		<p>
			You'll want to make a fresh Unity project to work with the importer. It is important that you don't use a newer version of Unity than what the game was built with, since that can cause 
			errors when trying to load your characters. The game is currently built with Unity <b>[{{unityVersion}}]</b>, but any <b>[{{unityVersionShort}}]</b> version should be compatible.
		</p>

		<p>
			It's also not recommended to use a much older version of Unity than this. With older versions, you may run into other errors when using the Importer itself, since some code used may 
			not be supported by the versions of C# or .NET that older versions of Unity use.
		</p>
        
		<b-img src="/images/importer/installation/unity-hub.jpg"></b-img>
		<p>
			For the project template, leave it on the basic 3D project that uses the built-in renderer. Give your project a name and pick the location where you want the project to live, 
			then click Create Project and wait for it to set things up.
		</p>
        
		<b-img src="/images/importer/installation/custom-package.jpg"></b-img>
		<p>
			Once the project has been initialized, the Unity Editor will open to a default scene. Now that we have a Unity project, we can load in the Character Importer package. To do that, 
			go to Assets -> Import Package -> Custom Package.	
		</p>
        
		<b-img src="/images/importer/installation/import-package.jpg"></b-img>
		<p>
			In the window that appears, navigate to where you've downloaded the Importer's unitypackage file, and select it. In the import popup, you can leave all the items checked and just click Import.
		</p>
        
		<b-img src="/images/importer/installation/project-settings.jpg"></b-img>
		<p>
			Something you may want to do is set the color space of your project to match what the game uses. Otherwise your character may look different here in the importer than 
			when they're in-game. To do that, go to Edit -> Project Settings. In the Player options under Other Settings, change the Color Space from Gamma to Linear.
		</p>
		
		<b-img src="/images/importer/installation/example-scene.jpg"></b-img>
		<p>
			The importer comes with an Examples folder with scenes for Anthro and Feral, which have versions of some of the Legacy characters that appear in the game. All of the game's 
			built-in characters are actually set up using this Character Importer as well, so players have access to the same tools and functionality for their models as the regular characters use.
		</p>
        
		<b-img src="/images/importer/installation/example-scene-pink.jpg"></b-img>
		<p>
			When opening the Example scenes in a new project, you may notice the character is pink. This is because the built-in characters use a Character Shader which was made using an optional 
			Unity package called Shader Graph. This is a free package but it's not installed by default, so Unity doesn't know how to render this shader right now. If you have your own shaders to use on 
			your character, or you just want to use a standard shader, then you don't need to do anything else. The Character Shader does provide some functionality like being able to adjust the clipping
			distance for the mesh, which is useful for hiding your character's head from the first-person or VR cameras so you don't see it in your view.
		</p>
        
		<b-img src="/images/importer/installation/package-manager.jpg"></b-img>
		<p>
            If you want to use the CharacterShader or just have a better view of the example scenes, go to Window -> Package Manager, and make sure the dropdown at the top left is set to Unity Registry. 
			Then scroll down until you find Shader Graph, and click Install. The materials in the example scenes should now be rendering correctly.
		</p>
	</div>
</template>

<script>
	import { mapState } from 'vuex';

export default {
		name: 'Installation',
		computed: {
			...mapState(['unityVersion', 'unityVersionShort']),
        }
}
</script>
